import React from 'react';
import styled from 'styled-components';

import { RadiusOption } from 'types/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import Imgix from 'shared/components/imgix';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import { useGetCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import { ConditionalLink } from 'src/utils/conditional-link';
import { Category } from '../categories-nav.types';

const radiusOptions = {
  [RadiusOption.rounded]: '9999px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

type StackedCardProps = {
  category: Category;
  style: RadiusOption;
  isMobileGrid: boolean;
};

export const StackedCard = ({ category, style, isMobileGrid }: StackedCardProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const { key, label, slug, customCategory } = category;

  const image = getCategoryPhoto(dispensary, category);
  const imgixParams = {
    height: 256,
  };

  const href = useGetCustomCategoryLink(customCategory, slug, key);

  return (
    <ConditionalLink url={href}>
      <Anchor href={href} data-testid='category-list-link'>
        <ImageWrapper $radius={style} $isMobileGrid={isMobileGrid}>
          <Image
            src={image}
            disableSrcSet
            imgixParams={imgixParams}
            htmlAttributes={{ alt: `${label} category image` }}
          />
        </ImageWrapper>

        <h3>{label}</h3>
      </Anchor>
    </ConditionalLink>
  );
};

const ImageWrapper = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #e3e7e9;
  border-radius: ${({ $radius }) => radiusOptions[$radius]};
  display: flex;
  height: 112px;
  justify-content: center;
  margin: 0 auto 8px;
  overflow: hidden;
  transition: box-shadow 0.1s;
  width: ${({ $radius, $isMobileGrid }) =>
    $isMobileGrid && [RadiusOption.square, RadiusOption.roundedRectangle].includes($radius) ? `100%` : `112px`};

  @media screen and (min-width: 600px) {
    height: 128px;
    width: 128px;
  }
`;

const Anchor = styled.a`
  display: block;
  font-size: 18px;
  font-weight: 600;
  line-height: ${22 / 18};
  text-align: center;
  white-space: nowrap;

  &:hover ${ImageWrapper} {
    box-shadow: 0 5px 14px #0000001a;
  }
`;

const Image = styled(Imgix)`
  display: block;
  width: 100%;
`;
